import { DesignPriceBrackets, ShippingTierForCurrency } from "../../../types";
import { useTranslation } from "react-i18next";
import { currencyToCountry } from "../../../utils/LocaleUtil";
import { useMemo } from "react";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { isAvailableInCurrency } from "./priceCalculatorUtils";
import { FormattedPriceWithExtraCredits, FormattedPrice } from "./types";
import { getFormattedCreditDetails } from "../../../utils/designUtils";

/**
 * Hook to calculate prices for a design based on quantity and pricing brackets
 * @param designPricing - Pricing structure including brackets and credits
 * @param productName - Name of the product being priced
 * @param qty - Quantity being ordered
 */
export const usePriceCalculator = (
  designPricing: DesignPriceBrackets | undefined,
  productName: string,
  qty: number,
  selectedShippingOption: ShippingTierForCurrency | null
) => {
  const { t } = useTranslation();
  const { FullCurrencyFormatter, getCurrency } = useCurrency();

  // Currency availability check
  const notAvailableInCurrency = useMemo(
    () => !isAvailableInCurrency(designPricing),
    [designPricing]
  );

  const hasMinimum = useMemo(
    () => qty >= (designPricing?.minQty ?? 0),
    [designPricing?.minQty, qty]
  );

  const hasReachedMaximum = useMemo(() => {
    return qty >= (designPricing?.baseProductPriceBrackets?.at(-1)?.qty ?? 0); // Optimization: using at(-1) instead of array access
  }, [designPricing?.baseProductPriceBrackets, qty]);

  /**
   * Processes and formats credit details, separating color-related credits from others
   * Returns an array of formatted credits with names and values
   */
  const formattedCredits = useMemo(() => {
    return getFormattedCreditDetails(
      designPricing?.creditsDetails ?? [],
      t,
      productName
    );
  }, [designPricing?.creditsDetails, t, productName]);

  /**
   * Calculates the base unit price without extra credits
   * Returns both the numeric price and formatted string
   */
  const rawUnitPrice: FormattedPrice = useMemo(() => {
    if (!hasMinimum) {
      return { price: 0, formatted: t("pricing.notMetMinimum") };
    }
    if (hasReachedMaximum) {
      return { price: 0, formatted: t("pricing.tailoredPrice") };
    }
    if (notAvailableInCurrency) {
      return {
        price: 0,
        formatted: t("pricing.notAvailableInCountry", {
          country: currencyToCountry[getCurrency()],
        }),
      };
    }

    let price = 0;

    designPricing?.baseProductPriceBrackets.some((pricing) => {
      if (qty < pricing.qty) {
        price = pricing.price;
        return true;
      }
      return false;
    });

    return { price, formatted: FullCurrencyFormatter?.format(price) };
  }, [
    hasMinimum,
    hasReachedMaximum,
    notAvailableInCurrency,
    designPricing?.baseProductPriceBrackets,
    FullCurrencyFormatter,
    t,
    getCurrency,
    qty,
  ]);

  /**
   * Calculates the additional price from extra credits
   */
  const creditUnitPrice: FormattedPriceWithExtraCredits = useMemo(() => {
    const extraCredits = designPricing?.extraCredits ?? 0;

    if (notAvailableInCurrency) {
      return { price: 0, formatted: "", extraCredits };
    }

    const price = designPricing?.priceOfExtraCredits ?? 0;

    return {
      price,
      formatted: FullCurrencyFormatter?.format(price),
      extraCredits,
    };
  }, [
    designPricing?.extraCredits,
    designPricing?.priceOfExtraCredits,
    notAvailableInCurrency,
    FullCurrencyFormatter,
  ]);

  /**
   * Combines raw unit price and credit price for total unit price
   */
  const unitPrice: FormattedPrice = useMemo(() => {
    if (notAvailableInCurrency) {
      return { price: 0, formatted: "" };
    }
    const price =
      designPricing?.unitPriceBrackets?.find((p) => qty < p.qty)?.price ?? 0;
    return { price, formatted: FullCurrencyFormatter?.format(price) };
  }, [
    notAvailableInCurrency,
    designPricing?.unitPriceBrackets,
    FullCurrencyFormatter,
    qty,
  ]);

  const shippingPrice = useMemo(() => {
    if (!selectedShippingOption) {
      return { price: 0, formatted: "" };
    }
    if (!hasMinimum) {
      return { price: 0, formatted: t("pricing.notMetMinimum") };
    }
    if (hasReachedMaximum) {
      return { price: 0, formatted: t("pricing.tailoredPrice") };
    }

    const price =
      (selectedShippingOption.priceBrackets.find(
        (bracket) => qty < bracket.orderBelow
      )?.price ?? 0) * qty;
    return { price, formatted: FullCurrencyFormatter?.format(price) };
  }, [
    selectedShippingOption,
    FullCurrencyFormatter,
    qty,
    hasMinimum,
    hasReachedMaximum,
    t,
  ]);

  /**
   * Calculates the final total price based on quantity
   */
  const totalPrice: FormattedPrice = useMemo(() => {
    if (notAvailableInCurrency) {
      return { price: 0, formatted: "" };
    }
    const price = unitPrice.price * qty + shippingPrice.price;
    return {
      price,
      formatted: FullCurrencyFormatter?.format(price),
    };
  }, [
    notAvailableInCurrency,
    unitPrice.price,
    qty,
    shippingPrice.price,
    FullCurrencyFormatter,
  ]);

  return {
    creditUnitPrice,
    formattedCredits,
    hasMinimum,
    hasReachedMaximum,
    notAvailableInCurrency,
    rawUnitPrice,
    totalPrice,
    unitPrice,
    shippingPrice,
  };
};
